import Dexie from "dexie";

export class PMSDatabase extends Dexie {
  constructor() {
    super("PMSDatabase");

    this.version(1).stores({
      users: "id, email, role, adminId, supervisorId, status",
      appraisals: "id, templateId, userId, status, dueDate, createdAt",
      templates: "id, type, createdBy, createdAt",
      comments: "id, appraisalId, author, timestamp",
      feedbackSnippets: "id, category, sentiment, createdBy",
      invites: "id, inviteCode, createdBy, expiryDate, status",
      notifications: "id, userId, type, read, timestamp",
      calendarEvents: "id, type, startDate, createdBy",
      goals: "id, appraisalId, userId, status, type",
      settings: "key",
    });

    // Add feedback table in v2 while preserving existing stores
    this.version(2).stores({
      users: "id, email, role, adminId, supervisorId, status",
      appraisals: "id, templateId, userId, status, dueDate, createdAt",
      templates: "id, type, createdBy, createdAt",
      comments: "id, appraisalId, author, timestamp",
      feedbackSnippets: "id, category, sentiment, createdBy",
      invites: "id, inviteCode, createdBy, expiryDate, status",
      notifications: "id, userId, type, read, timestamp",
      calendarEvents: "id, type, startDate, createdBy",
      goals: "id, appraisalId, userId, status, type",
      settings: "key",
      feedbacks:
        "id, receiverId, receiverRole, senderId, senderRole, createdAt",
    });

    // Add courses and allocations in v3
    this.version(3).stores({
      users: "id, email, role, adminId, supervisorId, status",
      appraisals: "id, templateId, userId, status, dueDate, createdAt",
      templates: "id, type, createdBy, createdAt",
      comments: "id, appraisalId, author, timestamp",
      feedbackSnippets: "id, category, sentiment, createdBy",
      invites: "id, inviteCode, createdBy, expiryDate, status",
      notifications: "id, userId, type, read, timestamp",
      calendarEvents: "id, type, startDate, createdBy",
      goals: "id, appraisalId, userId, status, type",
      settings: "key",
      feedbacks:
        "id, receiverId, receiverRole, senderId, senderRole, createdAt",
      courses: "id, title, link, description, createdBy, createdAt",
      courseAllocations:
        "id, courseId, userId, assignedBy, status, dueDate, assignedAt, updatedAt",
    });

    // Add comprehensive payroll system in v4
    this.version(4).stores({
      users: "id, email, role, adminId, supervisorId, status",
      appraisals: "id, templateId, userId, status, dueDate, createdAt",
      templates: "id, type, createdBy, createdAt",
      comments: "id, appraisalId, author, timestamp",
      feedbackSnippets: "id, category, sentiment, createdBy",
      invites: "id, inviteCode, createdBy, expiryDate, status",
      notifications: "id, userId, type, read, timestamp",
      calendarEvents: "id, type, startDate, createdBy",
      goals: "id, appraisalId, userId, status, type",
      settings: "key",
      feedbacks: "id, receiverId, receiverRole, senderId, senderRole, createdAt",
      courses: "id, title, link, description, createdBy, createdAt",
      courseAllocations: "id, courseId, userId, assignedBy, status, dueDate, assignedAt, updatedAt",
      // Payroll Tables
      payrollSettings: "id, companyName, payrollCycle, payDay, currency, fiscalYearStart",
      salaryStructures: "id, userId, basicSalary, effectiveDate, status",
      payrollPeriods: "id, periodName, startDate, endDate, status, processedAt",
      payslips: "id, userId, periodId, status, generatedAt, paidAt",
      earnings: "id, userId, type, amount, periodId, status",
      deductions: "id, userId, type, amount, periodId, status",
      deductionTypes: "id, name, category, isStatutory, isActive",
      earningTypes: "id, name, category, isTaxable, isActive",
      taxSlabs: "id, minIncome, maxIncome, rate, fiscalYear",
      bonuses: "id, userId, type, amount, status, approvedBy, periodId",
      reimbursements: "id, userId, category, amount, status, approvedBy, submittedAt",
      attendanceRecords: "id, userId, date, checkIn, checkOut, status",
      leaveBalances: "id, userId, leaveType, balance, year",
      leaveRequests: "id, userId, leaveType, startDate, endDate, status, approvedBy",
      loans: "id, userId, amount, emiAmount, remainingAmount, status, startDate",
      bankDetails: "id, oderId, accountNumber, bankName, ifscCode",
    });

    // US Payroll System v5 - Comprehensive US-based payroll with tax management
    this.version(5).stores({
      users: "id, email, role, adminId, supervisorId, status",
      appraisals: "id, templateId, userId, status, dueDate, createdAt",
      templates: "id, type, createdBy, createdAt",
      comments: "id, appraisalId, author, timestamp",
      feedbackSnippets: "id, category, sentiment, createdBy",
      invites: "id, inviteCode, createdBy, expiryDate, status",
      notifications: "id, userId, type, read, timestamp",
      calendarEvents: "id, type, startDate, createdBy",
      goals: "id, appraisalId, userId, status, type",
      settings: "key",
      feedbacks: "id, receiverId, receiverRole, senderId, senderRole, createdAt",
      courses: "id, title, link, description, createdBy, createdAt",
      courseAllocations: "id, courseId, userId, assignedBy, status, dueDate, assignedAt, updatedAt",
      
      // ============ CONFIGURATION & SETUP ============
      // Company Settings
      companySettings: "id, ein, companyName, legalName, address, state, createdAt",
      
      // Company Bank Accounts (Funding)
      companyBankAccounts: "id, bankName, accountNumber, routingNumber, accountType, isPrimary, status",
      
      // Tax Accounts (Federal, State, Local)
      taxAccounts: "id, jurisdiction, taxType, accountNumber, filingFrequency, status",
      
      // Pay Codes (Earning Types)
      payCodes: "id, code, name, category, isTaxable, isOvertime, multiplier, status",
      
      // Deduction Codes
      deductionCodes: "id, code, name, category, isPreTax, limit, status",
      
      // Pay Groups (Schedules)
      payGroups: "id, name, frequency, payDay, cutoffDays, status",
      
      // Payroll Automation Schedules
      payrollSchedules: "id, name, payGroupId, frequency, dayOfWeek, dayOfMonth, time, autoApprove, autoSubmit, notifyBefore, status, createdAt, updatedAt",
      
      // ============ EMPLOYEE FINANCIALS ============
      // Employee W-4 Data
      employeeW4: "id, userId, filingStatus, multipleJobs, dependentCredit, otherIncome, deductions, additionalWithholding, exempt, effectiveDate",
      
      // Employee Pay Info
      employeeCompensation: "id, userId, payType, payRate, payFrequency, workState, residentState, effectiveDate, status",
      
      // Employee Bank Accounts (Direct Deposit)
      employeeBankAccounts: "id, userId, bankName, accountNumber, routingNumber, accountType, depositType, amount, percentage, priority, status",
      
      // Employee Deductions
      employeeDeductions: "id, userId, deductionCodeId, amount, isPercentage, startDate, endDate, status",
      
      // Employee Tax Overrides (State/Local)
      employeeTaxOverrides: "id, userId, jurisdiction, additionalWithholding, exempt, effectiveDate",
      
      // ============ PAYROLL ENGINE ============
      // Payroll Settings (Company-wide)
      payrollSettings: "id, companyName, payrollCycle, payDay, currency, fiscalYearStart",
      
      // Payroll Runs
      payrollRuns: "id, payGroupId, periodStart, periodEnd, checkDate, runType, status, createdBy, createdAt, processedAt, submittedAt",
      
      // Payroll Run Items (Individual Employee Calculations)
      payrollRunItems: "id, payrollRunId, userId, grossPay, preTaxDeductions, taxableWages, federalTax, stateTax, localTax, socialSecurity, medicare, postTaxDeductions, netPay, status, createdAt",
      
      // Payroll Periods (Legacy support)
      payrollPeriods: "id, periodName, startDate, endDate, status, processedAt",
      
      // Salary Structures (Legacy support)
      salaryStructures: "id, userId, basicSalary, effectiveDate, status",
      
      // Payslips (Legacy support)
      payslips: "id, userId, periodId, status, generatedAt, paidAt",
      
      // Time Entries (from T&A)
      timeEntries: "id, userId, payrollRunId, date, payCodeId, hours, rate, amount, status",
      
      // ============ TAX MANAGEMENT ============
      // Tax Liability Ledger
      taxLiabilities: "id, payrollRunId, jurisdiction, taxType, amount, dueDate, status, paidDate",
      
      // Tax Filings
      taxFilings: "id, formType, jurisdiction, periodStart, periodEnd, amount, status, filedDate, acceptedDate",
      
      // Tax Payments
      taxPayments: "id, taxLiabilityId, amount, paymentMethod, confirmationNumber, paymentDate, status",
      
      // Year-End Forms (W-2, W-3)
      yearEndForms: "id, userId, formType, taxYear, status, generatedAt, distributedAt, filedAt",
      
      // ============ TREASURY & PAYMENTS ============
      // ACH Batches
      achBatches: "id, payrollRunId, batchType, totalAmount, effectiveDate, status, createdAt, sentAt, settledAt",
      
      // ACH Transactions
      achTransactions: "id, achBatchId, userId, amount, accountNumber, routingNumber, transactionType, status, returnCode, returnReason",
      
      // Manual Checks
      manualChecks: "id, payrollRunId, userId, checkNumber, amount, issueDate, status, voidDate, voidReason",
      
      // Payment Reversals
      paymentReversals: "id, originalPaymentId, paymentType, amount, reason, status, createdAt",
      
      // ============ LEGACY TABLES ============
      earnings: "id, userId, type, amount, periodId, status",
      deductions: "id, userId, type, amount, periodId, status",
      deductionTypes: "id, name, category, isStatutory, isActive",
      earningTypes: "id, name, category, isTaxable, isActive",
      taxSlabs: "id, minIncome, maxIncome, rate, fiscalYear",
      bonuses: "id, userId, type, amount, status, approvedBy, periodId",
      reimbursements: "id, userId, category, amount, status, approvedBy, submittedAt",
      attendanceRecords: "id, userId, date, checkIn, checkOut, status",
      leaveBalances: "id, userId, leaveType, balance, year",
      leaveRequests: "id, userId, leaveType, startDate, endDate, status, approvedBy",
      loans: "id, userId, amount, emiAmount, remainingAmount, status, startDate",
      bankDetails: "id, userId, accountNumber, bankName, ifscCode",
      taxSettings: "id, jurisdiction, taxType, rate, effectiveDate",
    });

    // ============================================================================
    // HRIS Compliance System v6 - Federal & State Forms, Policy Acknowledgments
    // ============================================================================
    this.version(6).stores({
      // Preserve all existing tables from v5
      users: "id, email, role, adminId, supervisorId, status",
      appraisals: "id, templateId, userId, status, dueDate, createdAt",
      templates: "id, type, createdBy, createdAt",
      comments: "id, appraisalId, author, timestamp",
      feedbackSnippets: "id, category, sentiment, createdBy",
      invites: "id, inviteCode, createdBy, expiryDate, status",
      notifications: "id, userId, type, read, timestamp",
      calendarEvents: "id, type, startDate, createdBy",
      goals: "id, appraisalId, userId, status, type",
      settings: "key",
      feedbacks: "id, receiverId, receiverRole, senderId, senderRole, createdAt",
      courses: "id, title, link, description, createdBy, createdAt",
      courseAllocations: "id, courseId, userId, assignedBy, status, dueDate, assignedAt, updatedAt",
      companySettings: "id, ein, companyName, legalName, address, state, createdAt",
      companyBankAccounts: "id, bankName, accountNumber, routingNumber, accountType, isPrimary, status",
      taxAccounts: "id, jurisdiction, taxType, accountNumber, filingFrequency, status",
      payCodes: "id, code, name, category, isTaxable, isOvertime, multiplier, status",
      deductionCodes: "id, code, name, category, isPreTax, limit, status",
      payGroups: "id, name, frequency, payDay, cutoffDays, status",
      payrollSchedules: "id, name, payGroupId, frequency, dayOfWeek, dayOfMonth, time, autoApprove, autoSubmit, notifyBefore, status, createdAt, updatedAt",
      employeeW4: "id, userId, filingStatus, multipleJobs, dependentCredit, otherIncome, deductions, additionalWithholding, exempt, effectiveDate",
      employeeCompensation: "id, userId, payType, payRate, payFrequency, workState, residentState, effectiveDate, status",
      employeeBankAccounts: "id, userId, bankName, accountNumber, routingNumber, accountType, depositType, amount, percentage, priority, status",
      employeeDeductions: "id, userId, deductionCodeId, amount, isPercentage, startDate, endDate, status",
      employeeTaxOverrides: "id, userId, jurisdiction, additionalWithholding, exempt, effectiveDate",
      payrollSettings: "id, companyName, payrollCycle, payDay, currency, fiscalYearStart",
      payrollRuns: "id, payGroupId, periodStart, periodEnd, checkDate, runType, status, createdBy, createdAt, processedAt, submittedAt",
      payrollRunItems: "id, payrollRunId, userId, grossPay, preTaxDeductions, taxableWages, federalTax, stateTax, localTax, socialSecurity, medicare, postTaxDeductions, netPay, status, createdAt",
      payrollPeriods: "id, periodName, startDate, endDate, status, processedAt",
      salaryStructures: "id, userId, basicSalary, effectiveDate, status",
      payslips: "id, userId, periodId, status, generatedAt, paidAt",
      timeEntries: "id, userId, payrollRunId, date, payCodeId, hours, rate, amount, status",
      taxLiabilities: "id, payrollRunId, jurisdiction, taxType, amount, dueDate, status, paidDate",
      taxFilings: "id, formType, jurisdiction, periodStart, periodEnd, amount, status, filedDate, acceptedDate",
      taxPayments: "id, taxLiabilityId, amount, paymentMethod, confirmationNumber, paymentDate, status",
      yearEndForms: "id, userId, formType, taxYear, status, generatedAt, distributedAt, filedAt",
      achBatches: "id, payrollRunId, batchType, totalAmount, effectiveDate, status, createdAt, sentAt, settledAt",
      achTransactions: "id, achBatchId, userId, amount, accountNumber, routingNumber, transactionType, status, returnCode, returnReason",
      manualChecks: "id, payrollRunId, userId, checkNumber, amount, issueDate, status, voidDate, voidReason",
      paymentReversals: "id, originalPaymentId, paymentType, amount, reason, status, createdAt",
      earnings: "id, userId, type, amount, periodId, status",
      deductions: "id, userId, type, amount, periodId, status",
      deductionTypes: "id, name, category, isStatutory, isActive",
      earningTypes: "id, name, category, isTaxable, isActive",
      taxSlabs: "id, minIncome, maxIncome, rate, fiscalYear",
      bonuses: "id, userId, type, amount, status, approvedBy, periodId",
      reimbursements: "id, userId, category, amount, status, approvedBy, submittedAt",
      attendanceRecords: "id, userId, date, checkIn, checkOut, status",
      leaveBalances: "id, userId, leaveType, balance, year",
      leaveRequests: "id, userId, leaveType, startDate, endDate, status, approvedBy",
      loans: "id, userId, amount, emiAmount, remainingAmount, status, startDate",
      bankDetails: "id, userId, accountNumber, bankName, ifscCode",
      taxSettings: "id, jurisdiction, taxType, rate, effectiveDate",

      // ============ HRIS COMPLIANCE FORMS ============
      // Form Templates (Admin-configurable form definitions)
      hrisForms: "id, formCode, formName, category, jurisdiction, version, isActive, effectiveDate, createdAt, updatedAt",
      
      // Employee Form Submissions
      formSubmissions: "id, userId, formId, formCode, formType, status, data, submittedAt, signedAt, signatureData, verifiedAt, verifiedBy, expiresAt",
      
      // I-9 Employment Eligibility Verification
      i9Submissions: "id, userId, section1Status, section1Data, section1SignedAt, section2Status, section2Data, section2CompletedAt, section2VerifiedBy, section2DueDate, section3Data, reverificationDate, everifyStatus, everifyCaseNumber, status, createdAt, updatedAt",
      
      // Document Uploads (for I-9 and other forms)
      documentUploads: "id, userId, formSubmissionId, documentType, listType, documentTitle, issuer, documentNumber, expirationDate, fileName, fileType, fileSize, fileData, uploadedAt, verifiedAt, verifiedBy, status",
      
      // State Tax Forms (DE-4 for California, etc.)
      stateTaxForms: "id, userId, formCode, state, filingStatus, allowances, additionalWithholding, exempt, effectiveDate, signedAt, signatureData, status",
      
      // ============ POLICY & NOTICE ACKNOWLEDGMENTS ============
      // Policy/Notice Definitions
      compliancePolicies: "id, policyCode, title, category, jurisdiction, content, requiresSignature, isActive, effectiveDate, version, createdAt, updatedAt",
      
      // Employee Policy Acknowledgments
      policyAcknowledgments: "id, userId, policyId, policyCode, acknowledgedAt, signatureData, ipAddress, userAgent, status",
      
      // ============ E-SIGNATURES ============
      eSignatures: "id, userId, documentType, documentId, signatureType, signatureData, legalText, signedAt, ipAddress, userAgent",
      
      // ============ TRAINING COMPLIANCE ============
      // Training Requirements (extends courses)
      trainingRequirements: "id, trainingCode, title, category, description, roleTypes, jurisdiction, frequencyType, frequencyMonths, dueWithinDays, isActive, courseId, createdAt",
      
      // Employee Training Assignments (compliance-specific)
      trainingAssignments: "id, userId, trainingRequirementId, courseAllocationId, status, dueDate, completedAt, certificateData, createdAt, updatedAt",
      
      // ============ COMPLIANCE TRACKING ============
      // Onboarding Checklist Items
      onboardingChecklist: "id, userId, itemCode, itemType, title, status, dueDate, completedAt, data, createdAt, updatedAt",
      
      // Compliance Audit Log
      complianceAuditLog: "id, userId, action, entityType, entityId, details, performedBy, performedAt, ipAddress",
    });
  }
}

export const db = new PMSDatabase();

// Add error handler for database open errors
db.on('blocked', () => {
  console.warn('Database upgrade blocked. Please close other tabs with this app.');
});

// Handle version change errors
db.open().catch(async (error) => {
  if (error.name === 'UpgradeError' || error.message?.includes('changing primary key') || error.message?.includes('Not yet support')) {
    console.warn('Database schema conflict detected. Clearing database...', error.message);
    try {
      await db.delete();
      console.log('Database deleted successfully. Reloading...');
      window.location.reload();
    } catch (deleteError) {
      console.error('Failed to delete database:', deleteError);
    }
  } else {
    console.error('Database open error:', error);
  }
});

let initializePromise;

// Handle database upgrade errors by clearing and reinitializing
async function handleDatabaseError(error) {
  if (error.name === 'UpgradeError' || error.message?.includes('changing primary key') || error.message?.includes('Not yet support')) {
    console.warn('Database schema conflict detected. Clearing and reinitializing database...');
    try {
      await db.delete();
      // Reload the page to reinitialize with fresh database
      window.location.reload();
    } catch (deleteError) {
      console.error('Failed to delete database:', deleteError);
      throw error;
    }
  } else {
    throw error;
  }
}

export async function initializeDatabase() {
  // Prevent double seeding when React StrictMode runs effects twice in dev
  if (initializePromise) return initializePromise;

  initializePromise = (async () => {
    try {
      const userCount = await db.users.count();
      if (userCount > 0) return;
    } catch (error) {
      await handleDatabaseError(error);
      return;
    }

    const bcrypt = await import("bcryptjs");
    const { v4: uuidv4 } = await import("uuid");
    const adminId = uuidv4();

    // Create admin user
    await db.users.add({
      id: adminId,
      email: "admin@company.com",
      passwordHash: await bcrypt.hash("Admin@123", 10),
      role: "admin",
      adminId: adminId,
      profile: {
        firstName: "System",
        lastName: "Administrator",
        photo: "",
        department: "Management",
        position: "Administrator",
        location: "HQ",
        phone: "555-0100",
        gender: "male",
        dateOfBirth: Date.now() - 30 * 365 * 24 * 60 * 60 * 1000,
        hireDate: Date.now() - 365 * 24 * 60 * 60 * 1000,
        workState: "CA",
        residentState: "CA",
      },
      status: "active",
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });

    // Create CEO user
    const ceoId = uuidv4();
    await db.users.add({
      id: ceoId,
      email: "ceo@company.com",
      passwordHash: await bcrypt.hash("Ceo@123", 10),
      role: "ceo",
      adminId: adminId,
      profile: {
        firstName: "James",
        lastName: "Wilson",
        photo: "",
        department: "Executive",
        position: "Chief Executive Officer",
        location: "HQ",
        phone: "555-0001",
        gender: "male",
        dateOfBirth: Date.now() - 45 * 365 * 24 * 60 * 60 * 1000,
        hireDate: Date.now() - 5 * 365 * 24 * 60 * 60 * 1000,
        workState: "CA",
        residentState: "CA",
      },
      status: "active",
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });

    // Create supervisor
    const supervisorId = uuidv4();
    await db.users.add({
      id: supervisorId,
      email: "supervisor@company.com",
      passwordHash: await bcrypt.hash("Super@123", 10),
      role: "supervisor",
      adminId: adminId,
      ceoId: ceoId,
      profile: {
        firstName: "Sarah",
        lastName: "Johnson",
        photo: "",
        department: "Engineering",
        position: "Team Lead",
        location: "HQ",
        phone: "555-0101",
        gender: "female",
        dateOfBirth: Date.now() - 35 * 365 * 24 * 60 * 60 * 1000,
        hireDate: Date.now() - 3 * 365 * 24 * 60 * 60 * 1000,
        workState: "CA",
        residentState: "CA",
      },
      status: "active",
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });

    // Create employees with diverse US states for realistic tax scenarios
    const employees = [
      { firstName: 'John', lastName: 'Smith', department: 'Engineering', position: 'Software Developer', gender: 'male', workState: 'CA', residentState: 'CA' },
      { firstName: 'Emily', lastName: 'Davis', department: 'Marketing', position: 'Marketing Specialist', gender: 'female', workState: 'NY', residentState: 'NJ' }, // Cross-state commuter
      { firstName: 'Michael', lastName: 'Brown', department: 'Engineering', position: 'Senior Developer', gender: 'male', workState: 'TX', residentState: 'TX' }, // No state income tax
      { firstName: 'Jessica', lastName: 'Wilson', department: 'HR', position: 'HR Coordinator', gender: 'female', workState: 'FL', residentState: 'FL' }, // No state income tax
      { firstName: 'David', lastName: 'Taylor', department: 'Sales', position: 'Sales Representative', gender: 'male', workState: 'WA', residentState: 'WA' }, // No state income tax
      { firstName: 'Priya', lastName: 'Patel', department: 'Product', position: 'Product Manager', gender: 'female', workState: 'MA', residentState: 'MA' },
      { firstName: 'Liam', lastName: 'Nguyen', department: 'Design', position: 'UX Designer', gender: 'male', workState: 'IL', residentState: 'IL' }
    ];

    const employeeIds = [];

    for (const emp of employees) {
      const id = uuidv4();
      await db.users.add({
        id,
        email: `${emp.firstName.toLowerCase()}.${emp.lastName.toLowerCase()}@company.com`,
        passwordHash: await bcrypt.hash("Employee@123", 10),
        role: "employee",
        adminId: adminId,
        supervisorId: supervisorId,
        profile: {
          firstName: emp.firstName,
          lastName: emp.lastName,
          photo: "",
          department: emp.department,
          position: emp.position,
          location: "HQ",
          phone: `555-01${Math.floor(Math.random() * 90) + 10}`,
          gender: emp.gender,
          dateOfBirth:
            Date.now() -
            (25 + Math.floor(Math.random() * 15)) * 365 * 24 * 60 * 60 * 1000,
          hireDate:
            Date.now() -
            Math.floor(Math.random() * 5) * 365 * 24 * 60 * 60 * 1000,
          workState: emp.workState,
          residentState: emp.residentState,
        },
        status: "active",
        createdAt: Date.now(),
        updatedAt: Date.now(),
      });
      employeeIds.push(id);
    }

    // Create default templates
    await seedDefaultTemplates(adminId);


    // Seed feedback/library content
    await db.feedbackSnippets.bulkAdd([
      { id: uuidv4(), category: 'performance', sentiment: 'positive', content: 'Consistently exceeds expectations on delivery timelines.', createdBy: adminId },
      { id: uuidv4(), category: 'performance', sentiment: 'positive', content: 'Demonstrates strong cross-team collaboration and communication.', createdBy: adminId },
      { id: uuidv4(), category: 'goal', sentiment: 'positive', content: 'On track to achieve quarterly OKRs with clear milestones.', createdBy: adminId },
      { id: uuidv4(), category: 'goal', sentiment: 'neutral', content: 'Needs clearer acceptance criteria for the next milestone.', createdBy: adminId },
      { id: uuidv4(), category: 'general', sentiment: 'positive', content: 'Provides actionable feedback to peers and supports onboarding.', createdBy: adminId },
      { id: uuidv4(), category: 'general', sentiment: 'positive', content: 'Shows initiative in addressing customer issues proactively.', createdBy: adminId }
    ]);

    // Seed goals for a few employees
    const goalSeeds = [
      {
        id: uuidv4(),
        userId: employeeIds[0],
        appraisalId: null,
        title: 'Refactor legacy module',
        description: 'Improve maintainability and test coverage to 80%',
        status: 'in_progress',
        priority: 'high',
        dueDate: Date.now() + 45 * 24 * 60 * 60 * 1000
      },
      {
        id: uuidv4(),
        userId: employeeIds[1],
        appraisalId: null,
        title: 'Launch campaign dashboard',
        description: 'Deliver V1 of marketing dashboard with KPIs',
        status: 'not_started',
        priority: 'medium',
        dueDate: Date.now() + 60 * 24 * 60 * 60 * 1000
      },
      {
        id: uuidv4(),
        userId: employeeIds[2],
        appraisalId: null,
        title: 'Mentor two juniors',
        description: 'Weekly pairing and monthly progress reviews',
        status: 'completed',
        priority: 'high',
        dueDate: Date.now() - 15 * 24 * 60 * 60 * 1000
      }
    ];
    await db.goals.bulkAdd(goalSeeds);

    // Seed appraisals covering states and visibility
    const appraisalSeeds = [
      {
        id: uuidv4(),
        templateId: null,
        userId: employeeIds[0],
        managerId: supervisorId,
        title: 'Supervisor Assigned - Pending Employee',
        type: 'annual',
        status: 'ready_to_evaluate',
        visibleToSupervisor: true,
        dueDate: Date.now() + 21 * 24 * 60 * 60 * 1000,
        createdAt: Date.now() - 7 * 24 * 60 * 60 * 1000,
        performanceAreas: [
          { competencyName: 'Quality', weight: 25, completedByEmployee: false, completedByManager: false },
          { competencyName: 'Delivery', weight: 25, completedByEmployee: false, completedByManager: false },
          { competencyName: 'Teamwork', weight: 25, completedByEmployee: false, completedByManager: false },
          { competencyName: 'Initiative', weight: 25, completedByEmployee: false, completedByManager: false }
        ],
        goals: { current: [], future: [], pdp: [] },
        progress: { performanceAreasComplete: false, goalsComplete: false, commentsComplete: false, overallPercent: 0 },
        scores: {}
      },
      {
        id: uuidv4(),
        templateId: null,
        userId: employeeIds[1],
        managerId: supervisorId,
        title: 'Supervisor Assigned - Employee Submitted',
        type: 'annual',
        status: 'employee_completed',
        visibleToSupervisor: true,
        dueDate: Date.now() + 14 * 24 * 60 * 60 * 1000,
        createdAt: Date.now() - 14 * 24 * 60 * 60 * 1000,
        employeeSubmittedAt: Date.now() - 1 * 24 * 60 * 60 * 1000,
        performanceAreas: [
          { competencyName: 'Quality', weight: 30, employeeScore: 4, completedByEmployee: true, completedByManager: false },
          { competencyName: 'Delivery', weight: 30, employeeScore: 5, completedByEmployee: true, completedByManager: false },
          { competencyName: 'Teamwork', weight: 20, employeeScore: 4, completedByEmployee: true, completedByManager: false },
          { competencyName: 'Initiative', weight: 20, employeeScore: 3, completedByEmployee: true, completedByManager: false }
        ],
        goals: { current: [], future: [], pdp: [] },
        progress: { performanceAreasComplete: true, goalsComplete: false, commentsComplete: false, overallPercent: 40 },
        scores: { employeeSelfTotal: 4.1 }
      },
      {
        id: uuidv4(),
        templateId: null,
        userId: employeeIds[2],
        managerId: supervisorId,
        title: 'Supervisor Assigned - Under Review',
        type: 'annual',
        status: 'manager_review',
        visibleToSupervisor: true,
        dueDate: Date.now() + 7 * 24 * 60 * 60 * 1000,
        createdAt: Date.now() - 21 * 24 * 60 * 60 * 1000,
        employeeSubmittedAt: Date.now() - 5 * 24 * 60 * 60 * 1000,
        performanceAreas: [
          { competencyName: 'Quality', weight: 25, employeeScore: 4, managerScore: 4, completedByEmployee: true, completedByManager: true },
          { competencyName: 'Delivery', weight: 25, employeeScore: 4, managerScore: 3, completedByEmployee: true, completedByManager: true },
          { competencyName: 'Teamwork', weight: 25, employeeScore: 5, managerScore: 4, completedByEmployee: true, completedByManager: true },
          { competencyName: 'Initiative', weight: 25, employeeScore: 3, managerScore: 3, completedByEmployee: true, completedByManager: true }
        ],
        goals: { current: [], future: [], pdp: [] },
        progress: { performanceAreasComplete: true, goalsComplete: false, commentsComplete: true, overallPercent: 67 },
        scores: { employeeSelfTotal: 4, managerTotal: 3.5 },
        comments: { employee: 'Submitted for review.', manager: 'Review in progress.' }
      },
      {
        id: uuidv4(),
        templateId: null,
        userId: employeeIds[3],
        managerId: null,
        title: 'Direct Appraisal - Finalized',
        type: 'direct',
        status: 'finalized',
        visibleToSupervisor: false,
        dueDate: Date.now() - 7 * 24 * 60 * 60 * 1000,
        createdAt: Date.now() - 30 * 24 * 60 * 60 * 1000,
        finalizedAt: Date.now() - 6 * 24 * 60 * 60 * 1000,
        employeeSubmittedAt: Date.now() - 6 * 24 * 60 * 60 * 1000,
        performanceAreas: [
          { competencyName: 'Customer Focus', weight: 50, employeeScore: 5, completedByEmployee: true, completedByManager: false },
          { competencyName: 'Process Adherence', weight: 50, employeeScore: 4, completedByEmployee: true, completedByManager: false }
        ],
        goals: { current: [], future: [], pdp: [] },
        progress: { performanceAreasComplete: true, goalsComplete: false, commentsComplete: true, overallPercent: 67 },
        scores: { employeeSelfTotal: 4.5, finalWeightedScore: 4.5 },
        comments: { employee: 'Completed as direct assignment.' }
      }
    ];
    await db.appraisals.bulkAdd(appraisalSeeds);

    // Seed payroll data
    await seedPayrollData(adminId, ceoId, supervisorId, employeeIds);
    
    console.log('Database initialized with default users');
    console.log('Admin: admin@company.com / Admin@123');
    console.log('CEO: ceo@company.com / Ceo@123');
    console.log('Supervisor: supervisor@company.com / Super@123');
    console.log('Employee: john.smith@company.com / Employee@123');

  })();

  return initializePromise;
}

async function seedDefaultTemplates(adminId) {
  const { v4: uuidv4 } = await import("uuid");

  const defaultTemplates = [
    {
      id: uuidv4(),
      name: "Annual Performance Review",
      description: "Standard annual performance evaluation",
      type: "annual",
      sections: [
        {
          id: uuidv4(),
          name: "Performance Areas",
          order: 1,
          competencies: [
            {
              id: uuidv4(),
              name: "Communication",
              description:
                "Ability to convey information clearly and effectively",
              weight: 20,
              scale: {
                type: "numeric",
                min: 0,
                max: 5,
                labels: {
                  0: "Weak",
                  1: "Needs Improvement",
                  2: "Below Expectations",
                  3: "Meets Expectations",
                  4: "Exceeds Expectations",
                  5: "Outstanding",
                },
              },
              feedbackThreshold: 1,
              allowManualInput: true,
            },
            {
              id: uuidv4(),
              name: "Problem Solving",
              description: "Ability to identify and resolve issues",
              weight: 20,
              scale: {
                type: "numeric",
                min: 0,
                max: 5,
                labels: {
                  0: "Weak",
                  1: "Needs Improvement",
                  2: "Below Expectations",
                  3: "Meets Expectations",
                  4: "Exceeds Expectations",
                  5: "Outstanding",
                },
              },
              feedbackThreshold: 1,
              allowManualInput: true,
            },
            {
              id: uuidv4(),
              name: "Accountability",
              description: "Takes ownership of responsibilities",
              weight: 20,
              scale: {
                type: "numeric",
                min: 0,
                max: 5,
                labels: {
                  0: "Weak",
                  1: "Needs Improvement",
                  2: "Below Expectations",
                  3: "Meets Expectations",
                  4: "Exceeds Expectations",
                  5: "Outstanding",
                },
              },
              feedbackThreshold: 1,
              allowManualInput: true,
            },
            {
              id: uuidv4(),
              name: "Dependability",
              description: "Reliability and consistency",
              weight: 20,
              scale: {
                type: "numeric",
                min: 0,
                max: 5,
                labels: {
                  0: "Weak",
                  1: "Needs Improvement",
                  2: "Below Expectations",
                  3: "Meets Expectations",
                  4: "Exceeds Expectations",
                  5: "Outstanding",
                },
              },
              feedbackThreshold: 1,
              allowManualInput: true,
            },
            {
              id: uuidv4(),
              name: "Punctuality",
              description: "Timeliness and attendance",
              weight: 20,
              scale: {
                type: "numeric",
                min: 0,
                max: 5,
                labels: {
                  0: "Weak",
                  1: "Needs Improvement",
                  2: "Below Expectations",
                  3: "Meets Expectations",
                  4: "Exceeds Expectations",
                  5: "Outstanding",
                },
              },
              feedbackThreshold: 1,
              allowManualInput: true,
            },
          ],
        },
        { id: uuidv4(), name: "Goals", order: 2 },
        { id: uuidv4(), name: "Comments", order: 3 },
      ],
      settings: {
        weightingEnabled: true,
        routingPath: [
          { role: "employee", action: "complete" },
          { role: "supervisor", action: "review" },
        ],
        notifications: { daysBefore: [30, 14, 7], enabled: true },
      },
      createdBy: adminId,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    },
    {
      id: uuidv4(),
      name: "New Hire 30-Day Review",
      description: "First month evaluation for new employees",
      type: "introductory",
      sections: [
        {
          id: uuidv4(),
          name: "Performance Areas",
          order: 1,
          competencies: [
            {
              id: uuidv4(),
              name: "Learning & Adaptation",
              description: "Ability to learn new processes and adapt",
              weight: 50,
              scale: {
                type: "numeric",
                min: 0,
                max: 5,
                labels: {
                  0: "Weak",
                  1: "Needs Improvement",
                  2: "Below Expectations",
                  3: "Meets Expectations",
                  4: "Exceeds Expectations",
                  5: "Outstanding",
                },
              },
              feedbackThreshold: 1,
              allowManualInput: true,
            },
            {
              id: uuidv4(),
              name: "Team Integration",
              description: "Collaboration with team members",
              weight: 50,
              scale: {
                type: "numeric",
                min: 0,
                max: 5,
                labels: {
                  0: "Weak",
                  1: "Needs Improvement",
                  2: "Below Expectations",
                  3: "Meets Expectations",
                  4: "Exceeds Expectations",
                  5: "Outstanding",
                },
              },
              feedbackThreshold: 1,
              allowManualInput: true,
            },
          ],
        },
      ],
      settings: {
        introductoryCycle: { enabled: true, days: [30, 60, 90] },
        weightingEnabled: true,
        routingPath: [
          { role: "employee", action: "complete" },
          { role: "supervisor", action: "review" },
        ],
        notifications: { daysBefore: [7, 3], enabled: true },
      },
      createdBy: adminId,
      createdAt: Date.now(),

      updatedAt: Date.now()
    },
    {
      id: uuidv4(),
      name: 'Direct Praise Snapshot',
      description: 'Quick direct appraisal without manager review',
      type: 'direct',
      sections: [
        {
          id: uuidv4(),
          name: 'Performance Areas',
          order: 1,
          competencies: [
            {
              id: uuidv4(),
              name: 'Customer Impact',
              description: 'Delivers noticeable value to customers',
              weight: 50,
              scale: { type: 'numeric', min: 0, max: 5, labels: { 0: 'Weak', 1: 'Needs Improvement', 2: 'Below Expectations', 3: 'Meets Expectations', 4: 'Exceeds Expectations', 5: 'Outstanding' } },
              feedbackThreshold: 1,
              allowManualInput: true
            },
            {
              id: uuidv4(),
              name: 'Ownership',
              description: 'Shows initiative and closes loops',
              weight: 50,
              scale: { type: 'numeric', min: 0, max: 5, labels: { 0: 'Weak', 1: 'Needs Improvement', 2: 'Below Expectations', 3: 'Meets Expectations', 4: 'Exceeds Expectations', 5: 'Outstanding' } },
              feedbackThreshold: 1,
              allowManualInput: true
            }
          ]
        },
        { id: uuidv4(), name: 'Comments', order: 2 }
      ],
      settings: {
        weightingEnabled: true,
        routingPath: [
          { role: 'employee', action: 'complete' }
        ],
        notifications: { daysBefore: [3], enabled: true }
      },
      createdBy: adminId,
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    {
      id: uuidv4(),
      name: 'Supervisor Self Review',
      description: 'Self-assessment for supervisors prior to calibration',
      type: 'supervisor',
      sections: [
        {
          id: uuidv4(),
          name: 'Leadership Competencies',
          order: 1,
          competencies: [
            {
              id: uuidv4(),
              name: 'Coaching',
              description: 'Develops and guides team members',
              weight: 40,
              scale: { type: 'numeric', min: 0, max: 5, labels: { 0: 'Weak', 1: 'Needs Improvement', 2: 'Below Expectations', 3: 'Meets Expectations', 4: 'Exceeds Expectations', 5: 'Outstanding' } },
              feedbackThreshold: 1,
              allowManualInput: true
            },
            {
              id: uuidv4(),
              name: 'Strategic Planning',
              description: 'Sets direction and aligns the team',
              weight: 30,
              scale: { type: 'numeric', min: 0, max: 5, labels: { 0: 'Weak', 1: 'Needs Improvement', 2: 'Below Expectations', 3: 'Meets Expectations', 4: 'Exceeds Expectations', 5: 'Outstanding' } },
              feedbackThreshold: 1,
              allowManualInput: true
            },
            {
              id: uuidv4(),
              name: 'Stakeholder Management',
              description: 'Manages expectations across stakeholders',
              weight: 30,
              scale: { type: 'numeric', min: 0, max: 5, labels: { 0: 'Weak', 1: 'Needs Improvement', 2: 'Below Expectations', 3: 'Meets Expectations', 4: 'Exceeds Expectations', 5: 'Outstanding' } },
              feedbackThreshold: 1,
              allowManualInput: true
            }
          ]
        },
        { id: uuidv4(), name: 'Goals', order: 2 },
        { id: uuidv4(), name: 'Comments', order: 3 }
      ],
      settings: {
        weightingEnabled: true,
        routingPath: [
          { role: 'supervisor', action: 'complete' }
        ],
        notifications: { daysBefore: [14, 7], enabled: true }
      },
      createdBy: adminId,
      createdAt: Date.now(),
      updatedAt: Date.now()
    }

    
        ];

  await db.templates.bulkAdd(defaultTemplates);
}

async function seedPayrollData(adminId, ceoId, supervisorId, employeeIds) {
  const { v4: uuidv4 } = await import("uuid");
  const allEmployeeIds = [ceoId, supervisorId, ...employeeIds];

  // ============ US PAYROLL REQUIRED DATA ============
  
  // Pay Groups (Required for US Payroll Run)
  const defaultPayGroupId = uuidv4();
  const payGroups = [
    {
      id: defaultPayGroupId,
      name: 'Default Bi-Weekly',
      description: 'Standard bi-weekly pay schedule for all employees',
      frequency: 'biweekly',
      payDay: 'friday',
      periodStartDay: 'sunday',
      cutoffDays: 3,
      processingDays: 2,
      directDepositLeadDays: 2,
      status: 'active',
      createdAt: Date.now()
    },
    {
      id: uuidv4(),
      name: 'Executive Monthly',
      description: 'Monthly pay schedule for executives',
      frequency: 'monthly',
      payDay: 'last_day',
      periodStartDay: 'first_day',
      cutoffDays: 5,
      processingDays: 3,
      directDepositLeadDays: 2,
      status: 'active',
      createdAt: Date.now()
    }
  ];
  await db.payGroups.bulkAdd(payGroups);

  // Employee Compensation Data (Required for US Payroll Run)
  const compensationData = [
    { userId: adminId, payType: 'salary', payRate: 95000, workState: 'CA', residentState: 'CA', department: 'Management' },
    { userId: ceoId, payType: 'salary', payRate: 250000, workState: 'CA', residentState: 'CA', department: 'Executive' },
    { userId: supervisorId, payType: 'salary', payRate: 120000, workState: 'CA', residentState: 'CA', department: 'Engineering' },
    { userId: employeeIds[0], payType: 'salary', payRate: 95000, workState: 'CA', residentState: 'CA', department: 'Engineering' },
    { userId: employeeIds[1], payType: 'salary', payRate: 85000, workState: 'NY', residentState: 'NJ', department: 'Marketing' },
    { userId: employeeIds[2], payType: 'salary', payRate: 110000, workState: 'TX', residentState: 'TX', department: 'Engineering' },
    { userId: employeeIds[3], payType: 'hourly', payRate: 35, workState: 'FL', residentState: 'FL', department: 'HR' },
    { userId: employeeIds[4], payType: 'salary', payRate: 78000, workState: 'WA', residentState: 'WA', department: 'Sales' },
    { userId: employeeIds[5], payType: 'salary', payRate: 105000, workState: 'MA', residentState: 'MA', department: 'Product' },
    { userId: employeeIds[6], payType: 'salary', payRate: 92000, workState: 'IL', residentState: 'IL', department: 'Design' }
  ];

  for (const comp of compensationData) {
    await db.employeeCompensation.add({
      id: uuidv4(),
      userId: comp.userId,
      payType: comp.payType,
      payRate: comp.payRate,
      payFrequency: comp.userId === ceoId ? 'monthly' : 'biweekly',
      payGroupId: comp.userId === ceoId ? payGroups[1].id : defaultPayGroupId,
      workState: comp.workState,
      residentState: comp.residentState,
      flsaStatus: comp.payType === 'salary' ? 'exempt' : 'nonexempt',
      effectiveDate: Date.now() - 365 * 24 * 60 * 60 * 1000,
      status: 'active',
      createdAt: Date.now(),
      updatedAt: Date.now()
    });
  }

  // Employee W-4 Data (Required for Federal Tax Withholding)
  // Filing statuses: 'single', 'married' (for jointly), 'marriedSeparate', 'headOfHousehold'
  const w4Data = [
    { userId: adminId, filingStatus: 'single', dependentCredit: 0, additionalWithholding: 0 },
    { userId: ceoId, filingStatus: 'married', dependentCredit: 4000, additionalWithholding: 0 },
    { userId: supervisorId, filingStatus: 'married', dependentCredit: 2000, additionalWithholding: 0 },
    { userId: employeeIds[0], filingStatus: 'single', dependentCredit: 0, additionalWithholding: 0 },
    { userId: employeeIds[1], filingStatus: 'single', dependentCredit: 0, additionalWithholding: 0 },
    { userId: employeeIds[2], filingStatus: 'married', dependentCredit: 4000, additionalWithholding: 0 },
    { userId: employeeIds[3], filingStatus: 'headOfHousehold', dependentCredit: 2000, additionalWithholding: 0 },
    { userId: employeeIds[4], filingStatus: 'single', dependentCredit: 0, additionalWithholding: 0 },
    { userId: employeeIds[5], filingStatus: 'married', dependentCredit: 2000, additionalWithholding: 0 },
    { userId: employeeIds[6], filingStatus: 'single', dependentCredit: 0, additionalWithholding: 0 }
  ];

  for (const w4 of w4Data) {
    await db.employeeW4.add({
      id: uuidv4(),
      userId: w4.userId,
      filingStatus: w4.filingStatus,
      multipleJobs: false,
      dependentCredit: w4.dependentCredit,
      otherIncome: 0,
      deductions: 0,
      additionalWithholding: w4.additionalWithholding,
      exempt: false,
      effectiveDate: Date.now() - 365 * 24 * 60 * 60 * 1000,
      signedAt: Date.now() - 365 * 24 * 60 * 60 * 1000,
      createdAt: Date.now(),
      updatedAt: Date.now()
    });
  }

  // Employee Bank Accounts for Direct Deposit
  const bankNames = ["Chase Bank", "Bank of America", "Wells Fargo", "Citibank", "US Bank", "Capital One", "PNC Bank", "TD Bank", "SunTrust"];
  for (let i = 0; i < allEmployeeIds.length; i++) {
    await db.employeeBankAccounts.add({
      id: uuidv4(),
      userId: allEmployeeIds[i],
      bankName: bankNames[i % bankNames.length],
      accountNumber: `****${String(1000 + i * 111).padStart(4, '0')}`,
      routingNumber: `0${String(21000000 + i * 100000).substring(0, 8)}`,
      accountType: 'checking',
      depositType: 'full',
      amount: null,
      percentage: 100,
      priority: 1,
      status: 'active',
      createdAt: Date.now()
    });
  }

  // Pay Codes (Earning Types for US Payroll)
  const payCodes = [
    { code: 'REG', name: 'Regular Pay', category: 'regular', calculationMethod: 'hourly', multiplier: 1, taxable: true },
    { code: 'OT', name: 'Overtime', category: 'overtime', calculationMethod: 'hourly', multiplier: 1.5, taxable: true },
    { code: 'DT', name: 'Double Time', category: 'overtime', calculationMethod: 'hourly', multiplier: 2, taxable: true },
    { code: 'HOL', name: 'Holiday Pay', category: 'premium', calculationMethod: 'hourly', multiplier: 1.5, taxable: true },
    { code: 'PTO', name: 'Paid Time Off', category: 'leave', calculationMethod: 'hourly', multiplier: 1, taxable: true },
    { code: 'SICK', name: 'Sick Leave', category: 'leave', calculationMethod: 'hourly', multiplier: 1, taxable: true },
    { code: 'BONUS', name: 'Bonus', category: 'supplemental', calculationMethod: 'fixed', multiplier: 1, taxable: true },
    { code: 'COMM', name: 'Commission', category: 'supplemental', calculationMethod: 'fixed', multiplier: 1, taxable: true }
  ];
  
  for (const pc of payCodes) {
    await db.payCodes.add({
      id: uuidv4(),
      code: pc.code,
      name: pc.name,
      type: 'earning',
      category: pc.category,
      calculationMethod: pc.calculationMethod,
      multiplier: pc.multiplier,
      taxable: pc.taxable,
      subjectToFICA: true,
      subjectToFUTA: true,
      includeInW2Box1: true,
      status: 'active',
      createdAt: Date.now()
    });
  }

  // Deduction Codes (for US Payroll)
  const deductionCodes = [
    { code: '401K', name: '401(k) Contribution', category: 'pre_tax', isPreTax: true, limit: 23000 },
    { code: 'ROTH', name: 'Roth 401(k)', category: 'post_tax', isPreTax: false, limit: 23000 },
    { code: 'HSA', name: 'Health Savings Account', category: 'pre_tax', isPreTax: true, limit: 4150 },
    { code: 'FSA', name: 'Flexible Spending Account', category: 'pre_tax', isPreTax: true, limit: 3200 },
    { code: 'DENTAL', name: 'Dental Insurance', category: 'pre_tax', isPreTax: true, limit: null },
    { code: 'VISION', name: 'Vision Insurance', category: 'pre_tax', isPreTax: true, limit: null },
    { code: 'HEALTH', name: 'Health Insurance', category: 'pre_tax', isPreTax: true, limit: null },
    { code: 'LIFE', name: 'Life Insurance', category: 'post_tax', isPreTax: false, limit: null },
    { code: 'GARNISH', name: 'Wage Garnishment', category: 'post_tax', isPreTax: false, limit: null }
  ];

  for (const dc of deductionCodes) {
    await db.deductionCodes.add({
      id: uuidv4(),
      code: dc.code,
      name: dc.name,
      type: 'deduction',
      category: dc.category,
      calculationMethod: 'fixed',
      taxDeferred: dc.isPreTax,
      reducesSSWages: dc.code === '401K' || dc.code === 'HSA',
      reducesMedicareWages: false,
      w2BoxCode: dc.code === '401K' ? 'D' : dc.code === 'ROTH' ? 'AA' : dc.code === 'HSA' ? 'W' : '',
      annualLimit: dc.limit,
      status: 'active',
      createdAt: Date.now()
    });
  }

  // Employee Deductions (401k, Health Insurance, etc.)
  const employeeDeductionSeeds = [
    { userId: ceoId, code: '401K', amount: 1500, isPercentage: false },
    { userId: ceoId, code: 'HEALTH', amount: 450, isPercentage: false },
    { userId: supervisorId, code: '401K', amount: 6, isPercentage: true },
    { userId: supervisorId, code: 'HEALTH', amount: 350, isPercentage: false },
    { userId: supervisorId, code: 'HSA', amount: 150, isPercentage: false },
    { userId: employeeIds[0], code: '401K', amount: 5, isPercentage: true },
    { userId: employeeIds[0], code: 'HEALTH', amount: 250, isPercentage: false },
    { userId: employeeIds[1], code: '401K', amount: 4, isPercentage: true },
    { userId: employeeIds[1], code: 'HEALTH', amount: 250, isPercentage: false },
    { userId: employeeIds[2], code: '401K', amount: 8, isPercentage: true },
    { userId: employeeIds[2], code: 'HSA', amount: 200, isPercentage: false },
    { userId: employeeIds[3], code: 'HEALTH', amount: 200, isPercentage: false },
    { userId: employeeIds[4], code: '401K', amount: 3, isPercentage: true },
    { userId: employeeIds[5], code: '401K', amount: 6, isPercentage: true },
    { userId: employeeIds[5], code: 'HEALTH', amount: 300, isPercentage: false },
    { userId: employeeIds[6], code: '401K', amount: 5, isPercentage: true },
    { userId: employeeIds[6], code: 'DENTAL', amount: 50, isPercentage: false }
  ];

  // Get deduction code IDs
  const savedDeductionCodes = await db.deductionCodes.toArray();
  for (const ed of employeeDeductionSeeds) {
    const deductionCode = savedDeductionCodes.find(dc => dc.code === ed.code);
    if (deductionCode) {
      await db.employeeDeductions.add({
        id: uuidv4(),
        userId: ed.userId,
        deductionCodeId: deductionCode.id,
        amount: ed.amount,
        isPercentage: ed.isPercentage,
        startDate: Date.now() - 365 * 24 * 60 * 60 * 1000,
        endDate: null,
        status: 'active',
        createdAt: Date.now()
      });
    }
  }

  // ============ LEGACY PAYROLL DATA (for backward compatibility) ============

  // Payroll Settings
  await db.payrollSettings.add({
    id: uuidv4(),
    companyName: "PerformanceHub Inc.",
    payrollCycle: "biweekly",
    payDay: 'friday',
    currency: "USD",
    fiscalYearStart: "January",
    workingDaysPerMonth: 22,
    overtimeRate: 1.5,
    taxCalculationMethod: "progressive",
    createdAt: Date.now(),
    updatedAt: Date.now()
  });

  // Company Settings for Company Tax Page
  await db.companySettings.add({
    id: '1',
    ein: '12-3456789',
    companyName: 'PerformanceHub Inc.',
    legalName: 'PerformanceHub Technologies Inc.',
    dba: 'PerformanceHub',
    entityType: 'ccorp',
    address: {
      street: '100 Tech Park Drive',
      city: 'San Francisco',
      state: 'CA',
      zip: '94105',
      country: 'US'
    },
    mailingAddress: {
      street: '100 Tech Park Drive',
      city: 'San Francisco',
      state: 'CA',
      zip: '94105',
      country: 'US'
    },
    sameAsPhysical: true,
    phone: '(415) 555-0100',
    email: 'payroll@performancehub.com',
    fiscalYearEnd: '12',
    createdAt: Date.now(),
    updatedAt: Date.now()
  });

  // Tax Accounts for Company Tax Page - covering all employee work states
  const taxAccounts = [
    // Federal Accounts
    {
      id: uuidv4(),
      jurisdiction: 'federal',
      taxType: 'withholding',
      state: '',
      accountNumber: 'FED-941-123456',
      filingFrequency: 'quarterly',
      depositSchedule: 'semiweekly',
      status: 'active',
      notes: 'Federal income tax withholding (Form 941)',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    {
      id: uuidv4(),
      jurisdiction: 'federal',
      taxType: 'futa',
      state: '',
      accountNumber: 'FUTA-940-123456',
      filingFrequency: 'annual',
      depositSchedule: 'quarterly',
      status: 'active',
      notes: 'Federal Unemployment Tax (Form 940)',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    // California - Has state income tax, SDI, SUI
    {
      id: uuidv4(),
      jurisdiction: 'state',
      taxType: 'withholding',
      state: 'CA',
      accountNumber: 'CA-DE-9876543',
      filingFrequency: 'quarterly',
      depositSchedule: 'semiweekly',
      status: 'active',
      notes: 'California Personal Income Tax withholding (DE 9)',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    {
      id: uuidv4(),
      jurisdiction: 'state',
      taxType: 'sui',
      state: 'CA',
      accountNumber: 'CA-SUI-9876543',
      filingFrequency: 'quarterly',
      depositSchedule: 'quarterly',
      status: 'active',
      notes: 'California State Unemployment Insurance',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    {
      id: uuidv4(),
      jurisdiction: 'state',
      taxType: 'sdi',
      state: 'CA',
      accountNumber: 'CA-SDI-9876543',
      filingFrequency: 'quarterly',
      depositSchedule: 'quarterly',
      status: 'active',
      notes: 'California State Disability Insurance (0.9%)',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    // New York - Has state income tax, SDI, SUI, local tax possible
    {
      id: uuidv4(),
      jurisdiction: 'state',
      taxType: 'withholding',
      state: 'NY',
      accountNumber: 'NY-WHT-5551234',
      filingFrequency: 'quarterly',
      depositSchedule: 'monthly',
      status: 'active',
      notes: 'New York State Income Tax withholding',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    {
      id: uuidv4(),
      jurisdiction: 'state',
      taxType: 'sui',
      state: 'NY',
      accountNumber: 'NY-SUI-5551234',
      filingFrequency: 'quarterly',
      depositSchedule: 'quarterly',
      status: 'active',
      notes: 'New York State Unemployment Insurance',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    {
      id: uuidv4(),
      jurisdiction: 'state',
      taxType: 'sdi',
      state: 'NY',
      accountNumber: 'NY-SDI-5551234',
      filingFrequency: 'quarterly',
      depositSchedule: 'quarterly',
      status: 'active',
      notes: 'New York State Disability Insurance (0.5%)',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    // New Jersey - Has state income tax, SDI, SUI (for cross-state commuter)
    {
      id: uuidv4(),
      jurisdiction: 'state',
      taxType: 'withholding',
      state: 'NJ',
      accountNumber: 'NJ-WHT-4441234',
      filingFrequency: 'quarterly',
      depositSchedule: 'monthly',
      status: 'active',
      notes: 'New Jersey State Income Tax withholding',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    {
      id: uuidv4(),
      jurisdiction: 'state',
      taxType: 'sdi',
      state: 'NJ',
      accountNumber: 'NJ-SDI-4441234',
      filingFrequency: 'quarterly',
      depositSchedule: 'quarterly',
      status: 'active',
      notes: 'New Jersey State Disability Insurance (0.14%)',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    // Texas - NO state income tax, only SUI
    {
      id: uuidv4(),
      jurisdiction: 'state',
      taxType: 'sui',
      state: 'TX',
      accountNumber: 'TX-SUI-7891234',
      filingFrequency: 'quarterly',
      depositSchedule: 'quarterly',
      status: 'active',
      notes: 'Texas State Unemployment Tax (No income tax)',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    // Florida - NO state income tax, only SUI
    {
      id: uuidv4(),
      jurisdiction: 'state',
      taxType: 'sui',
      state: 'FL',
      accountNumber: 'FL-SUI-6661234',
      filingFrequency: 'quarterly',
      depositSchedule: 'quarterly',
      status: 'active',
      notes: 'Florida State Unemployment Tax (No income tax)',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    // Washington - NO state income tax, has PFML
    {
      id: uuidv4(),
      jurisdiction: 'state',
      taxType: 'sui',
      state: 'WA',
      accountNumber: 'WA-SUI-3331234',
      filingFrequency: 'quarterly',
      depositSchedule: 'quarterly',
      status: 'active',
      notes: 'Washington State Unemployment Tax (No income tax)',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    {
      id: uuidv4(),
      jurisdiction: 'state',
      taxType: 'pfml',
      state: 'WA',
      accountNumber: 'WA-PFML-3331234',
      filingFrequency: 'quarterly',
      depositSchedule: 'quarterly',
      status: 'active',
      notes: 'Washington Paid Family & Medical Leave (0.58%)',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    // Massachusetts - Has flat state income tax (5%)
    {
      id: uuidv4(),
      jurisdiction: 'state',
      taxType: 'withholding',
      state: 'MA',
      accountNumber: 'MA-WHT-2221234',
      filingFrequency: 'quarterly',
      depositSchedule: 'monthly',
      status: 'active',
      notes: 'Massachusetts State Income Tax (5% flat rate)',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    {
      id: uuidv4(),
      jurisdiction: 'state',
      taxType: 'sui',
      state: 'MA',
      accountNumber: 'MA-SUI-2221234',
      filingFrequency: 'quarterly',
      depositSchedule: 'quarterly',
      status: 'active',
      notes: 'Massachusetts State Unemployment Insurance',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    // Illinois - Has flat state income tax (4.95%)
    {
      id: uuidv4(),
      jurisdiction: 'state',
      taxType: 'withholding',
      state: 'IL',
      accountNumber: 'IL-WHT-1111234',
      filingFrequency: 'quarterly',
      depositSchedule: 'monthly',
      status: 'active',
      notes: 'Illinois State Income Tax (4.95% flat rate)',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    {
      id: uuidv4(),
      jurisdiction: 'state',
      taxType: 'sui',
      state: 'IL',
      accountNumber: 'IL-SUI-1111234',
      filingFrequency: 'quarterly',
      depositSchedule: 'quarterly',
      status: 'active',
      notes: 'Illinois State Unemployment Insurance',
      createdAt: Date.now(),
      updatedAt: Date.now()
    }
  ];
  await db.taxAccounts.bulkAdd(taxAccounts);

  // Earning Types - US Standard Components
  const earningTypes = [
    { id: uuidv4(), name: "Base Salary", code: "BASE", category: "fixed", isTaxable: true, isActive: true, description: "Base salary component" },
    { id: uuidv4(), name: "Regular Hours", code: "REG", category: "fixed", isTaxable: true, isActive: true, description: "Regular hourly wages" },
    { id: uuidv4(), name: "Overtime", code: "OT", category: "variable", isTaxable: true, isActive: true, description: "Overtime pay (1.5x or 2x)" },
    { id: uuidv4(), name: "Holiday Pay", code: "HOL", category: "variable", isTaxable: true, isActive: true, description: "Holiday premium pay" },
    { id: uuidv4(), name: "PTO Payout", code: "PTO", category: "variable", isTaxable: true, isActive: true, description: "Paid time off payout" },
    { id: uuidv4(), name: "Performance Bonus", code: "PERF_BONUS", category: "variable", isTaxable: true, isActive: true, description: "Performance-based bonus" },
    { id: uuidv4(), name: "Annual Bonus", code: "ANN_BONUS", category: "variable", isTaxable: true, isActive: true, description: "Yearly bonus" },
    { id: uuidv4(), name: "Commission", code: "COMM", category: "variable", isTaxable: true, isActive: true, description: "Sales commission" },
    { id: uuidv4(), name: "Referral Bonus", code: "REF_BONUS", category: "variable", isTaxable: true, isActive: true, description: "Employee referral bonus" },
    { id: uuidv4(), name: "Sign-On Bonus", code: "SIGN_BONUS", category: "variable", isTaxable: true, isActive: true, description: "New hire sign-on bonus" },
  ];
  await db.earningTypes.bulkAdd(earningTypes);

  // Deduction Types - US Standard
  const deductionTypes = [
    { id: uuidv4(), name: "Federal Income Tax", code: "FIT", category: "statutory", isStatutory: true, isActive: true, description: "Federal income tax withholding" },
    { id: uuidv4(), name: "Social Security", code: "SS", category: "statutory", isStatutory: true, isActive: true, description: "Social Security tax (6.2%)" },
    { id: uuidv4(), name: "Medicare", code: "MED", category: "statutory", isStatutory: true, isActive: true, description: "Medicare tax (1.45%)" },
    { id: uuidv4(), name: "State Income Tax", code: "SIT", category: "statutory", isStatutory: true, isActive: true, description: "State income tax withholding" },
    { id: uuidv4(), name: "401(k) Contribution", code: "401K", category: "pretax", isStatutory: false, isActive: true, description: "401(k) retirement contribution" },
    { id: uuidv4(), name: "Health Insurance", code: "HEALTH", category: "pretax", isStatutory: false, isActive: true, description: "Health insurance premium" },
    { id: uuidv4(), name: "Dental Insurance", code: "DENTAL", category: "pretax", isStatutory: false, isActive: true, description: "Dental insurance premium" },
    { id: uuidv4(), name: "Vision Insurance", code: "VISION", category: "pretax", isStatutory: false, isActive: true, description: "Vision insurance premium" },
    { id: uuidv4(), name: "HSA Contribution", code: "HSA", category: "pretax", isStatutory: false, isActive: true, description: "Health Savings Account" },
    { id: uuidv4(), name: "FSA Contribution", code: "FSA", category: "pretax", isStatutory: false, isActive: true, description: "Flexible Spending Account" },
    { id: uuidv4(), name: "Roth 401(k)", code: "ROTH", category: "posttax", isStatutory: false, isActive: true, description: "Roth 401(k) after-tax contribution" },
    { id: uuidv4(), name: "Garnishment", code: "GARN", category: "posttax", isStatutory: false, isActive: true, description: "Court-ordered wage garnishment" },
  ];
  await db.deductionTypes.bulkAdd(deductionTypes);

  // Tax Slabs (Progressive Tax - Annual Income Brackets)
  // Adjusted for realistic salary ranges ($12K-$45K monthly = $144K-$540K annual)
  const taxSlabs = [
    { id: uuidv4(), minIncome: 0, maxIncome: 250000, rate: 0, fiscalYear: "2025", description: "No tax - up to $250K" },
    { id: uuidv4(), minIncome: 250001, maxIncome: 500000, rate: 10, fiscalYear: "2025", description: "10% tax bracket" },
    { id: uuidv4(), minIncome: 500001, maxIncome: 1000000, rate: 20, fiscalYear: "2025", description: "20% tax bracket" },
    { id: uuidv4(), minIncome: 1000001, maxIncome: 2000000, rate: 30, fiscalYear: "2025", description: "30% tax bracket" },
    { id: uuidv4(), minIncome: 2000001, maxIncome: 999999999, rate: 35, fiscalYear: "2025", description: "35% tax bracket" },
  ];
  await db.taxSlabs.bulkAdd(taxSlabs);

  // Salary Structures for employees
  const salaryData = [
    { userId: ceoId, basic: 25000, hra: 10000, ta: 2000, ma: 1500, sa: 6500 }, // CEO: $45,000/month
    { userId: supervisorId, basic: 12000, hra: 4800, ta: 1500, ma: 1000, sa: 2700 }, // Supervisor: $22,000/month
    { userId: employeeIds[0], basic: 8000, hra: 3200, ta: 1000, ma: 800, sa: 2000 }, // Employee 1: $15,000/month
    { userId: employeeIds[1], basic: 7500, hra: 3000, ta: 1000, ma: 800, sa: 1700 }, // Employee 2: $14,000/month
    { userId: employeeIds[2], basic: 9000, hra: 3600, ta: 1000, ma: 800, sa: 2600 }, // Employee 3: $17,000/month (Senior)
    { userId: employeeIds[3], basic: 6500, hra: 2600, ta: 800, ma: 600, sa: 1500 }, // Employee 4: $12,000/month
    { userId: employeeIds[4], basic: 7000, hra: 2800, ta: 900, ma: 700, sa: 1600 }, // Employee 5: $13,000/month
    { userId: employeeIds[5], basic: 8500, hra: 3400, ta: 1000, ma: 800, sa: 2300 }, // Employee 6: $16,000/month
    { userId: employeeIds[6], basic: 8000, hra: 3200, ta: 1000, ma: 800, sa: 2000 }, // Employee 7: $15,000/month
  ];

  for (const sal of salaryData) {
    await db.salaryStructures.add({
      id: uuidv4(),
      userId: sal.userId,
      basicSalary: sal.basic,
      hra: sal.hra,
      transportAllowance: sal.ta,
      medicalAllowance: sal.ma,
      specialAllowance: sal.sa,
      grossSalary: sal.basic + sal.hra + sal.ta + sal.ma + sal.sa,
      effectiveDate: Date.now() - 180 * 24 * 60 * 60 * 1000,
      status: "active",
      createdAt: Date.now(),
      updatedAt: Date.now()
    });
  }

  // Add a salary hike for one employee mid-month (for demo of pro-rated salary)
  // Employee 1 gets a 15% salary hike effective on the 10th of current month
  const currentMonthStart = new Date();
  currentMonthStart.setDate(10);
  currentMonthStart.setHours(0, 0, 0, 0);
  
  const oldBasic = 8000;
  const hikePercent = 0.15;
  const newBasic = Math.round(oldBasic * (1 + hikePercent));
  const newHra = Math.round(newBasic * 0.4);
  const newTa = 1100;
  const newMa = 900;
  const newSa = Math.round(newBasic * 0.25);
  
  await db.salaryStructures.add({
    id: uuidv4(),
    userId: employeeIds[0],
    basicSalary: newBasic,
    hra: newHra,
    transportAllowance: newTa,
    medicalAllowance: newMa,
    specialAllowance: newSa,
    grossSalary: newBasic + newHra + newTa + newMa + newSa,
    effectiveDate: currentMonthStart.getTime(),
    status: "active",
    previousSalaryId: null,
    hikePercentage: 15,
    hikeReason: "Annual Performance Review",
    createdAt: Date.now(),
    updatedAt: Date.now()
  });

  // Legacy Bank Details for employees (for backward compatibility with old payroll pages)
  const legacyBankNames = ["Chase Bank", "Bank of America", "Wells Fargo", "Citibank", "US Bank"];
  for (let i = 0; i < allEmployeeIds.length; i++) {
    await db.bankDetails.add({
      id: uuidv4(),
      userId: allEmployeeIds[i],
      accountNumber: `****${String(1000 + i * 111).padStart(4, '0')}`,
      bankName: legacyBankNames[i % legacyBankNames.length],
      ifscCode: `BANK${String(i + 1).padStart(4, '0')}`,
      accountType: "Savings",
      isPrimary: true,
      createdAt: Date.now()
    });
  }

  // Leave Balances - Create single record per user with nested leave type objects
  // This matches the expected structure in LeaveBalance.jsx component
  for (const userId of allEmployeeIds) {
    const annualUsed = Math.floor(Math.random() * 8);
    const sickUsed = Math.floor(Math.random() * 4);
    const casualUsed = Math.floor(Math.random() * 3);
    
    await db.leaveBalances.add({
      id: uuidv4(),
      userId,
      year: new Date().getFullYear(),
      annual: { total: 21, used: annualUsed, pending: 0, carryForward: Math.min(5, annualUsed) },
      sick: { total: 12, used: sickUsed, pending: 0, carryForward: 0 },
      casual: { total: 6, used: casualUsed, pending: 0, carryForward: 0 },
      maternity: { total: 180, used: 0, pending: 0, carryForward: 0 },
      paternity: { total: 15, used: 0, pending: 0, carryForward: 0 },
      compensatory: { total: Math.floor(Math.random() * 3), used: 0, pending: 0, carryForward: 0 },
      createdAt: Date.now()
    });
  }

  // Payroll Periods (last 6 months - bi-weekly periods)
  const periods = [];
  const currentYear = new Date().getFullYear();
  
  // Generate bi-weekly periods for last 6 months
  for (let i = 5; i >= 0; i--) {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    const periodEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0); // Last day of month
    const periodStart = new Date(date.getFullYear(), date.getMonth(), 1);
    const checkDate = new Date(periodEnd);
    checkDate.setDate(checkDate.getDate() + 5); // Pay date 5 days after period end
    
    const periodId = uuidv4();
    
    periods.push({
      id: periodId,
      periodName: periodStart.toLocaleString('default', { month: 'long', year: 'numeric' }),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
      startDate: periodStart.getTime(),
      endDate: periodEnd.getTime(),
      status: i === 0 ? "processing" : "completed",
      processedAt: i === 0 ? null : periodEnd.getTime() + 24 * 60 * 60 * 1000,
      processedBy: i === 0 ? null : adminId,
      totalGross: 0,
      totalDeductions: 0,
      totalNet: 0,
      employeeCount: allEmployeeIds.length,
      createdAt: Date.now()
    });

    // Note: Payroll runs are not seeded - they should only be created when user actually runs payroll
  }
  await db.payrollPeriods.bulkAdd(periods);

  // US Payroll Tax Constants for 2025
  const SS_RATE = 0.062; // 6.2%
  const MEDICARE_RATE = 0.0145; // 1.45%
  const SS_WAGE_BASE = 168600;
  
  // State tax rates (simplified)
  const STATE_TAX_RATES = {
    'CA': 0.093, // California ~9.3% average
    'NY': 0.0685, // New York ~6.85%
    'NJ': 0.0637, // New Jersey ~6.37%
    'TX': 0, // No state income tax
    'FL': 0, // No state income tax
    'WA': 0, // No state income tax
    'MA': 0.05, // Massachusetts 5% flat
    'IL': 0.0495 // Illinois 4.95% flat
  };

  // Federal tax brackets 2025 (simplified calculation)
  const calculateFederalTax = (annualIncome, filingStatus) => {
    const brackets = filingStatus === 'married' ? [
      { min: 0, max: 23200, rate: 0.10 },
      { min: 23200, max: 94300, rate: 0.12 },
      { min: 94300, max: 201050, rate: 0.22 },
      { min: 201050, max: 383900, rate: 0.24 },
      { min: 383900, max: Infinity, rate: 0.32 }
    ] : [
      { min: 0, max: 11600, rate: 0.10 },
      { min: 11600, max: 47150, rate: 0.12 },
      { min: 47150, max: 100525, rate: 0.22 },
      { min: 100525, max: 191950, rate: 0.24 },
      { min: 191950, max: Infinity, rate: 0.32 }
    ];
    
    let tax = 0;
    let remaining = annualIncome;
    
    for (const bracket of brackets) {
      if (remaining <= 0) break;
      const taxableInBracket = Math.min(remaining, bracket.max - bracket.min);
      tax += taxableInBracket * bracket.rate;
      remaining -= taxableInBracket;
    }
    
    return tax;
  };

  // Generate US-style payslips for completed periods
  for (let periodIdx = 0; periodIdx < periods.length; periodIdx++) {
    const period = periods[periodIdx];
    if (period.status !== "completed") continue;
    
    for (let i = 0; i < compensationData.length; i++) {
      const comp = compensationData[i];
      const w4 = w4Data[i];
      
      // Calculate gross pay based on pay type and frequency
      const payFrequency = comp.userId === ceoId ? 'monthly' : 'biweekly';
      const periodsPerYear = payFrequency === 'monthly' ? 12 : 26;
      
      let grossPay;
      if (comp.payType === 'salary') {
        grossPay = comp.payRate / periodsPerYear;
      } else {
        // Hourly: assume 80 hours for bi-weekly
        const hours = payFrequency === 'biweekly' ? 80 : 173.33;
        grossPay = comp.payRate * hours;
      }
      
      // Pre-tax deductions (401k, health insurance from employee deductions)
      const empDeductions = employeeDeductionSeeds.filter(d => d.userId === comp.userId);
      let preTax401k = 0;
      let preTaxHealth = 0;
      let preTaxHSA = 0;
      
      for (const ded of empDeductions) {
        if (ded.code === '401K') {
          preTax401k = ded.isPercentage ? grossPay * (ded.amount / 100) : ded.amount;
        } else if (ded.code === 'HEALTH') {
          preTaxHealth = ded.amount;
        } else if (ded.code === 'HSA') {
          preTaxHSA = ded.amount;
        }
      }
      
      const totalPreTax = preTax401k + preTaxHealth + preTaxHSA;
      const taxableGross = grossPay - totalPreTax;
      const annualTaxable = taxableGross * periodsPerYear;
      
      // Calculate taxes
      const filingStatus = w4.filingStatus === 'married' ? 'married' : 'single';
      const annualFederalTax = calculateFederalTax(annualTaxable, filingStatus);
      const federalTax = Math.round((annualFederalTax / periodsPerYear + (w4.additionalWithholding || 0)) * 100) / 100;
      
      // Social Security (6.2% up to wage base)
      const socialSecurity = Math.round(taxableGross * SS_RATE * 100) / 100;
      
      // Medicare (1.45%)
      const medicare = Math.round(taxableGross * MEDICARE_RATE * 100) / 100;
      
      // State tax
      const stateRate = STATE_TAX_RATES[comp.workState] || 0;
      const stateTax = Math.round(taxableGross * stateRate * 100) / 100;
      
      // State-specific additions (SDI for CA, NY)
      let sdiTax = 0;
      if (comp.workState === 'CA') sdiTax = Math.round(taxableGross * 0.009 * 100) / 100;
      if (comp.workState === 'NY') sdiTax = Math.round(taxableGross * 0.005 * 100) / 100;
      
      const totalTaxes = federalTax + socialSecurity + medicare + stateTax + sdiTax;
      const netPay = Math.round((grossPay - totalPreTax - totalTaxes) * 100) / 100;
      
      // Create US-style payslip
      await db.payslips.add({
        id: uuidv4(),
        userId: comp.userId,
        periodId: period.id,
        payrollRunId: null, // Will be set when user runs actual payroll
        periodName: period.periodName,
        month: period.month,
        year: period.year,
        payFrequency,
        
        // US Earnings breakdown
        earnings: {
          regular: grossPay,
          overtime: 0,
          bonus: 0,
          commission: 0,
          holiday: 0,
          pto: 0
        },
        grossPay: Math.round(grossPay * 100) / 100,
        
        // Pre-tax deductions
        preTaxDeductions: {
          retirement401k: preTax401k,
          healthInsurance: preTaxHealth,
          hsa: preTaxHSA,
          fsa: 0,
          dental: 0,
          vision: 0
        },
        totalPreTax: Math.round(totalPreTax * 100) / 100,
        taxableGross: Math.round(taxableGross * 100) / 100,
        
        // Taxes (US style)
        taxes: {
          federalIncome: federalTax,
          socialSecurity: socialSecurity,
          medicare: medicare,
          stateIncome: stateTax,
          stateName: comp.workState,
          sdi: sdiTax,
          localTax: 0
        },
        totalTaxes: Math.round(totalTaxes * 100) / 100,
        
        // Post-tax deductions
        postTaxDeductions: {
          roth401k: 0,
          lifeInsurance: 0,
          garnishment: 0,
          other: 0
        },
        totalPostTax: 0,
        
        // Net pay
        netPay,
        
        // W-4 info used
        w4Info: {
          filingStatus: w4.filingStatus,
          dependentCredit: w4.dependentCredit,
          additionalWithholding: w4.additionalWithholding
        },
        
        // Tax jurisdiction
        workState: comp.workState,
        residentState: comp.residentState,
        
        // YTD totals (accumulated)
        ytd: {
          grossPay: Math.round(grossPay * (6 - periodIdx) * 100) / 100,
          federalTax: Math.round(federalTax * (6 - periodIdx) * 100) / 100,
          socialSecurity: Math.round(socialSecurity * (6 - periodIdx) * 100) / 100,
          medicare: Math.round(medicare * (6 - periodIdx) * 100) / 100,
          stateTax: Math.round(stateTax * (6 - periodIdx) * 100) / 100,
          retirement401k: Math.round(preTax401k * (6 - periodIdx) * 100) / 100,
          netPay: Math.round(netPay * (6 - periodIdx) * 100) / 100
        },
        
        // Legacy fields for backward compatibility
        grossEarnings: Math.round(grossPay * 100) / 100,
        grossSalary: Math.round(grossPay * 100) / 100,
        totalDeductions: Math.round((totalPreTax + totalTaxes) * 100) / 100,
        netSalary: netPay,
        workingDays: 22,
        daysWorked: 22,
        lop: 0,
        
        status: "paid",
        generatedAt: period.processedAt,
        paidAt: period.processedAt + 24 * 60 * 60 * 1000,
        paymentMethod: "direct_deposit",
        createdAt: Date.now()
      });

      // Note: payrollRunItems are only created when user runs actual payroll
    }
  }

  // Bonus records
  const bonusSeeds = [
    { userId: employeeIds[2], type: "performance", amount: 2000, reason: "Q3 Performance Excellence", status: "paid", periodId: periods[3].id },
    { userId: employeeIds[0], type: "referral", amount: 500, reason: "Employee Referral Bonus", status: "paid", periodId: periods[4].id },
    { userId: supervisorId, type: "annual", amount: 5000, reason: "Annual Performance Bonus", status: "approved", periodId: periods[5].id },
    { userId: employeeIds[1], type: "spot", amount: 300, reason: "Spot Award for Project Delivery", status: "pending", periodId: periods[5].id },
  ];
  for (const bonus of bonusSeeds) {
    await db.bonuses.add({
      id: uuidv4(),
      ...bonus,
      approvedBy: bonus.status !== "pending" ? adminId : null,
      approvedAt: bonus.status !== "pending" ? Date.now() - 7 * 24 * 60 * 60 * 1000 : null,
      createdAt: Date.now(),
      updatedAt: Date.now()
    });
  }

  // Seed Tax Liabilities and Payroll Runs for completed pay periods
  const taxLiabilitySeeds = [];
  const payrollRunSeeds = [];
  const completedPeriods = periods.filter(p => p.status === 'completed');
  
  for (const period of completedPeriods) {
    const payrollRunId = uuidv4();
    
    // Calculate approximate amounts based on seed payslip data
    const numEmployees = compensationData.length;
    const avgGross = 5000; // Average gross per employee per period
    const totalGross = avgGross * numEmployees;
    
    // Federal Income Tax Withholding (approx 15% average)
    const federalWithholding = Math.round(avgGross * numEmployees * 0.15 * 100) / 100;
    
    // FICA (SS 6.2% + Medicare 1.45%) x 2 for employer match = 15.3%
    const ficaTotal = Math.round(avgGross * numEmployees * 0.153 * 100) / 100;
    
    // State withholding (average 5%)
    const stateWithholding = Math.round(avgGross * numEmployees * 0.05 * 100) / 100;
    
    const totalTaxes = federalWithholding + ficaTotal + stateWithholding;
    const totalNet = totalGross - totalTaxes - (totalGross * 0.08); // 8% for pre-tax deductions
    
    // Create payroll run record
    payrollRunSeeds.push({
      id: payrollRunId,
      periodId: period.id,
      periodName: period.periodName,
      payGroupId: 'all',
      payGroupName: 'All Employees',
      payFrequency: 'biweekly',
      periodStart: period.startDate,
      periodEnd: period.endDate,
      checkDate: period.endDate + 3 * 24 * 60 * 60 * 1000,
      employeeCount: numEmployees,
      totalGross: Math.round(totalGross * 100) / 100,
      totalTaxes: Math.round(totalTaxes * 100) / 100,
      totalDeductions: Math.round(totalGross * 0.08 * 100) / 100,
      totalNet: Math.round(totalNet * 100) / 100,
      totalEmployerTaxes: Math.round(ficaTotal / 2 * 100) / 100, // Half of FICA is employer portion
      status: 'completed',
      processedBy: adminId,
      processedAt: period.processedAt,
      approvedBy: adminId,
      approvedAt: period.processedAt + 60 * 60 * 1000,
      createdAt: period.processedAt - 24 * 60 * 60 * 1000
    });
    
    // Create due dates based on period - older ones could be paid or overdue
    const periodDate = new Date(period.endDate);
    const federalDueDate = new Date(periodDate);
    federalDueDate.setDate(federalDueDate.getDate() + 15);
    
    const stateDueDate = new Date(periodDate);
    stateDueDate.setDate(stateDueDate.getDate() + 30);
    
    // Determine status based on due date
    const now = Date.now();
    const getFederalStatus = () => {
      // If due date has passed, mark as paid (assume taxes are paid on time)
      if (federalDueDate.getTime() < now) return 'paid';
      return 'pending'; // Not yet due
    };
    
    const getStateStatus = () => {
      // If due date has passed, mark as paid (assume taxes are paid on time)
      if (stateDueDate.getTime() < now) return 'paid';
      return 'pending'; // Not yet due
    };
    
    // Federal withholding liability
    taxLiabilitySeeds.push({
      id: uuidv4(),
      payrollRunId,
      jurisdiction: 'federal',
      taxType: 'withholding',
      amount: federalWithholding,
      dueDate: federalDueDate.getTime(),
      status: getFederalStatus(),
      paidDate: getFederalStatus() === 'paid' ? federalDueDate.getTime() + 5 * 24 * 60 * 60 * 1000 : null,
      createdAt: period.processedAt
    });
    
    // FICA liability
    taxLiabilitySeeds.push({
      id: uuidv4(),
      payrollRunId,
      jurisdiction: 'federal',
      taxType: 'fica',
      amount: ficaTotal,
      dueDate: federalDueDate.getTime(),
      status: getFederalStatus(),
      paidDate: getFederalStatus() === 'paid' ? federalDueDate.getTime() + 5 * 24 * 60 * 60 * 1000 : null,
      createdAt: period.processedAt
    });
    
    // State withholding (California)
    taxLiabilitySeeds.push({
      id: uuidv4(),
      payrollRunId,
      jurisdiction: 'state',
      state: 'CA',
      taxType: 'withholding',
      amount: stateWithholding,
      dueDate: stateDueDate.getTime(),
      status: getStateStatus(),
      paidDate: getStateStatus() === 'paid' ? stateDueDate.getTime() + 5 * 24 * 60 * 60 * 1000 : null,
      createdAt: period.processedAt
    });
  }
  
  // Add tax liabilities to database
  for (const liability of taxLiabilitySeeds) {
    await db.taxLiabilities.add(liability);
  }
  
  // Add payroll runs to database
  for (const run of payrollRunSeeds) {
    await db.payrollRuns.add(run);
  }

  // Reimbursement records
  const reimbursementCategories = ["travel", "medical", "equipment", "training", "meals", "other"];
  const reimbursementSeeds = [
    { userId: employeeIds[0], category: "travel", amount: 450, description: "Client visit travel expenses", status: "approved", receiptUrl: "" },
    { userId: employeeIds[1], category: "training", amount: 299, description: "Online course subscription", status: "pending", receiptUrl: "" },
    { userId: supervisorId, category: "equipment", amount: 150, description: "Ergonomic keyboard", status: "approved", receiptUrl: "" },
    { userId: employeeIds[3], category: "medical", amount: 200, description: "Annual health checkup", status: "pending", receiptUrl: "" },
    { userId: employeeIds[2], category: "meals", amount: 85, description: "Team lunch meeting", status: "rejected", receiptUrl: "", rejectionReason: "Missing receipt" },
  ];
  for (const reimb of reimbursementSeeds) {
    await db.reimbursements.add({
      id: uuidv4(),
      ...reimb,
      submittedAt: Date.now() - Math.floor(Math.random() * 14) * 24 * 60 * 60 * 1000,
      approvedBy: reimb.status === "approved" ? adminId : null,
      approvedAt: reimb.status === "approved" ? Date.now() - 2 * 24 * 60 * 60 * 1000 : null,
      createdAt: Date.now()
    });
  }

  // Loan records
  await db.loans.add({
    id: uuidv4(),
    userId: employeeIds[0],
    loanType: "personal",
    amount: 5000,
    emiAmount: 500,
    tenure: 10,
    remainingAmount: 3500,
    paidEmis: 3,
    remainingEmis: 7,
    interestRate: 8,
    status: "active",
    startDate: Date.now() - 90 * 24 * 60 * 60 * 1000,
    endDate: Date.now() + 210 * 24 * 60 * 60 * 1000,
    approvedBy: adminId,
    approvedAt: Date.now() - 95 * 24 * 60 * 60 * 1000,
    createdAt: Date.now()
  });

  // Attendance Records (last 60 days to cover multiple pay periods, including today)
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize to start of day
  
  // Include ALL employees for attendance records
  for (const userId of allEmployeeIds) {
    // Generate attendance for last 60 days INCLUDING today (covers ~2 months for various pay period selections)
    for (let daysAgo = 0; daysAgo <= 60; daysAgo++) {
      const date = new Date(today);
      date.setDate(today.getDate() - daysAgo);
      
      // Skip weekends
      if (date.getDay() === 0 || date.getDay() === 6) continue;
      
      const isPresent = Math.random() > 0.1;
      const checkInDate = new Date(date);
      checkInDate.setHours(9, Math.floor(Math.random() * 30), 0);
      const checkOutDate = new Date(date);
      checkOutDate.setHours(17 + Math.floor(Math.random() * 2), Math.floor(Math.random() * 60), 0);
      
      // Calculate actual work hours (between 7-10 hours for variation)
      const workHrs = isPresent ? 7 + Math.random() * 3 : 0;
      const otHrs = isPresent && Math.random() > 0.8 ? Math.floor(Math.random() * 3) : 0;
      
      await db.attendanceRecords.add({
        id: uuidv4(),
        userId,
        date: date.getTime(),
        checkIn: isPresent ? checkInDate.getTime() : null,
        checkOut: isPresent ? checkOutDate.getTime() : null,
        status: isPresent ? "present" : "absent",
        workHours: workHrs,
        overtime: otHrs,
        createdAt: Date.now()
      });
    }
  }

  // Leave Requests
  const leaveRequestSeeds = [
    { userId: employeeIds[0], leaveType: "annual", startDate: Date.now() + 14 * 24 * 60 * 60 * 1000, endDate: Date.now() + 16 * 24 * 60 * 60 * 1000, reason: "Family vacation", status: "approved" },
    { userId: employeeIds[1], leaveType: "sick", startDate: Date.now() - 5 * 24 * 60 * 60 * 1000, endDate: Date.now() - 4 * 24 * 60 * 60 * 1000, reason: "Medical appointment", status: "approved" },
    { userId: employeeIds[2], leaveType: "personal", startDate: Date.now() + 7 * 24 * 60 * 60 * 1000, endDate: Date.now() + 7 * 24 * 60 * 60 * 1000, reason: "Personal work", status: "pending" },
    { userId: supervisorId, leaveType: "annual", startDate: Date.now() + 30 * 24 * 60 * 60 * 1000, endDate: Date.now() + 35 * 24 * 60 * 60 * 1000, reason: "Annual leave", status: "pending" },
  ];
  for (const leave of leaveRequestSeeds) {
    await db.leaveRequests.add({
      id: uuidv4(),
      ...leave,
      days: Math.ceil((leave.endDate - leave.startDate) / (24 * 60 * 60 * 1000)) + 1,
      approvedBy: leave.status === "approved" ? supervisorId : null,
      approvedAt: leave.status === "approved" ? Date.now() - 24 * 60 * 60 * 1000 : null,
      createdAt: Date.now()
    });
  }

  console.log('Payroll data seeded successfully with US payroll taxes (Federal, SS, Medicare, State)');
  console.log(`Seeded attendance records from ${new Date(today.getTime() - 60 * 24 * 60 * 60 * 1000).toDateString()} to ${today.toDateString()}`);
  console.log(`Seeded payroll periods for year ${currentYear}`);
}
